
































































































import { defineComponent, ref, computed, watch, watchEffect, Ref } from '@vue/composition-api';
import Mousetrap from 'mousetrap';
import { use, providers, components } from 'vue-media-annotator';
import type { MediaController } from 'vue-media-annotator/src/components/annotators/mediaControllerType';
// import type { TextData } from 'vue-media-annotator/src/layers/TextLayer';
import type { FrameDataTrack } from 'vue-media-annotator/src/layers/LayerTypes';

import store from '@/store';
import { copyToClipboard, dummyFunction } from '@/utils';
import { SimpleFrameData, RectBounds, Dataset } from '@/types';
import actions from '@/controller/actions';
import {
  trackMap,
  intervalTree,
  sortedTracks,
  removeTrack,
  selectedTrackId,
  selectTrack,
  filteredTracks,
  visibleModes,
  toggleVisibleMode,
  imageData,
  updateImageData,
} from '@/controller/state';
import Scorer from './Scorer.vue';
import { useClient } from '@/use';

// TODO: Remove once https://github.com/Kitware/dive/pull/1026 is addressed
export interface TextData {
  selected: boolean;
  editing: boolean | string;
  type: string;
  confidence: number;
  text: string;
  x: number;
  y: number;
  offsetY?: number;
  offsetX?: number;
  currentPair: boolean;
}

const { useStyling, useTimeObserver } = use;
const { provideAnnotator } = providers;
const { ImageAnnotator, Controls, LayerManager } = components;

interface MousetrapBinding {
  bind: string | string[];
  handler(e: Mousetrap.ExtendedKeyboardEvent, combo: string): void;
  type?: string;
}

// Subtype the Vue component to allow access to what we need
interface ImageAnnotatorType {
  data: { ready: boolean };
  mediaController: MediaController;
}

const annotationColors = [
  'gray',
  'blueviolet',
  'red',
  'darkcyan',
  'green',
  'darkorange',
  'brown',
  'coral',
  'darkblue',
  'darkcyan',
  'blueviolet',
  'crimson',
  'chocolate',
  'cadeblue',
  'pink',
  'firebrick',
  'darkgreen',
  'darkviolet',
  'darkorange',
  'darkred',
  'dimgray',
  'darkturquoise',
];

export default defineComponent({
  name: 'Annotator',
  components: {
    ImageAnnotator,
    Controls,
    Scorer,
    LayerManager,
  },
  setup() {
    const client = useClient();

    const annotatorRef = ref<ImageAnnotatorType | null>(null);
    const showDataConsole = computed({
      get: () => store.state.application.showDataConsole,
      set: (val: boolean) => store.commit.application.setShowDataConsole(val),
    });

    const brightness = ref(1.0);
    const brightnessTickSize = 0.1;
    const brightnessMin = 0;
    const brightnessMax = 2;

    const dataset = computed(() => store.state.dataset.dataset);
    const datasetId = computed(() => store.state.dataset.datasetId);

    const annotationClasses = computed(() => store.getters.application.annotationClasses);

    const currentFrame = computed(() => store.getters.dataset.currentFrame);
    const currentFrameIndex = computed(() => store.state.dataset.currentFrameIndex);
    const currentFrameAnnotations = computed(() => store.getters.dataset.currentFrameAnnotations);

    // Returns a MediaController once the component is ready
    const mediaController = computed(() => {
      if (!annotatorRef.value) { return {} as MediaController; }
      return annotatorRef.value.mediaController;
    });

    // Must be done in this component, so it has access to mediaController
    const annotatorReady = computed(() => annotatorRef.value?.data.ready as boolean);
    watchEffect(() => {
      if (datasetId.value && !annotatorReady.value) {
        store.commit.dataset.setLoading(true);
        return;
      }

      if (currentFrameIndex.value !== null && mediaController.value !== null) {
        if (store.state.dataset.loading) {
          store.commit.dataset.setLoading(false);
        }

        mediaController.value.seek(currentFrameIndex.value);
      }
    });

    function seekFrame(diff: number) {
      if (currentFrameIndex.value === null) { return; }
      actions.changeFrame(currentFrameIndex.value + diff);
    }

    const editingMode: Ref<false | 'rectangle'> = ref('rectangle');
    const keyboardBindings = [
      { bind: 'e', handler: () => seekFrame(-1) },
      { bind: 'r', handler: () => seekFrame(1) },
      {
        bind: ['del', 'z'],
        handler: () => {
          if (store.getters.isScorerOrAnnotator) {
            actions.removeCurrentAnnotation();
          }
        },
      },
      {
        bind: 'ctrl',
        handler: () => {
          if (store.getters.isScorerOrAnnotator) {
            actions.setDrawingAnnotation(
              !store.state.dataset.drawingAnnotation,
            );
          }
        },
      },
      {
        bind: 'c',
        handler: () => {
          if (store.getters.isScorerOrAnnotator) {
            actions.rotateAnnotationClass(1);
          }
        },
      },
      {
        bind: 'x',
        handler: () => {
          if (store.getters.isScorerOrAnnotator) {
            actions.rotateAnnotationClass(-1);
          }
        },
      },
      { bind: ',', handler: () => { brightness.value -= brightnessTickSize; } },
      { bind: '.', handler: () => { brightness.value += brightnessTickSize; } },
      { bind: 'space', handler: toggleVisibleMode },
      // { bind: 'tab', handler: () => selectAnnotation(1) },
      // { bind: 'shift+tab', handler: () => selectAnnotation(-1) },
    ];

    // Prevent all other space keypresses
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === ' ' && e.target === document.body) {
        e.preventDefault();
      }
    });

    // Perform binding
    keyboardBindings.forEach(({ bind, handler, type }: MousetrapBinding) => { Mousetrap.bind(bind, handler, type); });

    // Set styling
    const { stateStyling, typeStyling } = useStyling({ markChangesPending: dummyFunction });
    stateStyling.standard.strokeWidth = 1;
    stateStyling.selected.strokeWidth = 2;

    // Create a color mapping based on the current dataset annotation classes
    const colorMapping = computed<{[key: string]: string}>(() => {
      const classes = annotationClasses.value || [];
      return classes.reduce((obj, cls, i) => ({ ...obj, [cls]: annotationColors[i] }), {});
    });

    // Replace only typeStyling color function
    typeStyling.value.color = (type: string) => {
      const defaultColor = 'white';
      const annotations = currentFrameAnnotations.value;
      if (annotations === null) {
        return defaultColor;
      }

      return colorMapping.value[type] || defaultColor;
    };

    function handleSelectTrack(trackId: number | null, editing: boolean) {
      if (!store.getters.isScorerOrAnnotator) {
        return;
      }

      selectTrack(trackId, editing);

      // Set in store
      actions.setSelectedAnnotation(trackId, true);
    }

    // Must live here due to visibleModes
    function handleUpdateRectBounds(frame: number, flick: number, bounds: RectBounds) {
      actions.updateAnnotationBounds(bounds);

      // If annotations were hidden, show them after update
      if (!visibleModes.value.length) {
        toggleVisibleMode();
      }
    }

    // Create a custom computed component, so that imageData isn't always re-computed
    watch([dataset, datasetId], (val, oldVal) => {
      type tupleType = [Dataset | null, string | null];
      const [curDataset, curDatasetId] = val as tupleType;
      const [oldDataset, oldDatasetId] = oldVal as tupleType;

      const newDataset = curDatasetId !== oldDatasetId;
      const datasetEntryDeleted = curDataset?.length !== oldDataset?.length;
      if (newDataset || datasetEntryDeleted) {
        updateImageData(curDataset);
      }
    }, { immediate: true });

    // Manually set ready to false in annotator so it doesn't throw an error
    // when `seek` is called above
    // TODO: Remove this once this is fixed upstream
    watch(imageData, () => {
      if (annotatorRef.value?.data) {
        annotatorRef.value.data.ready = false;
      }
    });

    async function loadImage(imageDataItem: SimpleFrameData, img: HTMLImageElement) {
      const data = await client.getVideoFrame(imageDataItem.path);
      store.commit.dataset.setFrameImageFile({ index: imageDataItem.index, file: data });

      // eslint-disable-next-line no-param-reassign
      img.src = URL.createObjectURL(data);
    }

    function formatText(track: FrameDataTrack): TextData[] | null {
      const [type, trackConfidence] = track.confidencePairs![0];
      const fullTrack = trackMap.get(track.trackId);
      if (!track.features?.bounds || fullTrack === undefined || currentFrameIndex.value === null) {
        return null;
      }

      const { bounds } = track.features;
      const modelConfidence = fullTrack.getFeature(currentFrameIndex.value)[0]?.attributes?.confidence as number | undefined;
      const confidence = modelConfidence || trackConfidence;

      return [{
        selected: track.selected,
        editing: track.editing,
        type,
        confidence,
        text: `${type}: ${confidence.toFixed(2)}`,
        x: bounds[2],
        y: bounds[1], // updated below
        currentPair: true,
      }];
    }

    const { time, updateTime, initialize } = useTimeObserver();
    initialize({ frameRate: 1, originalFps: 1 });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const emptyFunction = () => undefined as any;
    const providerHandler: providers.Handler = {
      ...providers.dummyHandler(emptyFunction),
      trackSelect: handleSelectTrack,
      removeTrack: (trackIds) => trackIds.forEach((trackId) => removeTrack(trackId)),
      updateRectBounds: handleUpdateRectBounds,
    };
    const providerState: providers.State = {
      ...providers.dummyState(),
      editingMode,
      time,
      enabledTracks: filteredTracks,
      intervalTree,
      trackMap,
      filteredTracks,
      typeStyling,
      selectedTrackId,
      stateStyles: stateStyling,
      visibleModes,
    };

    provideAnnotator(providerState, providerHandler);

    return {
      annotatorRef,
      brightness,
      brightnessTickSize,
      brightnessMin,
      brightnessMax,
      showDataConsole,
      currentFrame,
      currentFrameIndex,
      datasetId,
      copyToClipboard,
      layerProps: {
        editingMode,
        intervalTree,
        selectedTrackId,
        stateStyling,
        typeStyling,
        trackMap,
        tracks: sortedTracks,
        visibleModes,
        selectedKey: ref(''),
        featurePointing: ref(false),
        featurePointingTarget: ref(null),
      },
      formatText,
      imageData,
      loadImage,
      updateTime,
      frameRate: time.frameRate,
      seekFrame,
      handleSelectTrack,
      handleUpdateRectBounds,
    };
  },
});
